import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

/**
 * SideHintObserver
 * - Observes any element that has a `data-hint` attribute
 * - When a section becomes prominent in viewport, shows a minimal side popup
 * - Popup text is typed character-by-character
 * - Optional per-section attributes:
 *   - data-hint:            string message to type
 *   - data-hint-side:       'right' | 'left' (default: right)
 *   - data-hint-delay:      ms to wait before showing (default: 300)
 *   - data-hint-speed:      ms per character (default: 20)
 *   - data-hint-duration:   ms to auto-hide after typed (default: 6000, 0 to disable)
 */
export default function SideHintObserver() {
  const HINT_DEBUG = false;
  const debugLog = (...args) => { if (HINT_DEBUG) console.log(...args); };
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [side, setSide] = useState('right');
  const [typedText, setTypedText] = useState('');
  const [fullText, setFullText] = useState('');
  const [mounted, setMounted] = useState(false);
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const lastHintIdRef = useRef(null);
  const observerRef = useRef(null);
  const scrollTickingRef = useRef(false);
  const shownHintsRef = useRef(new Set());

  // Clean timers
  const clearTimers = () => {
    if (showTimeoutRef.current) { clearTimeout(showTimeoutRef.current); showTimeoutRef.current = null; }
    if (hideTimeoutRef.current) { clearTimeout(hideTimeoutRef.current); hideTimeoutRef.current = null; }
    if (typingIntervalRef.current) { clearInterval(typingIntervalRef.current); typingIntervalRef.current = null; }
  };

  const startTyping = (text, speed = 20, duration = 6000) => {
    clearTimers();
    setTypedText('');
    let i = 0;
    typingIntervalRef.current = setInterval(() => {
      i += 1;
      setTypedText(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
        if (duration > 0) {
          hideTimeoutRef.current = setTimeout(() => setVisible(false), duration);
        }
      }
    }, Math.max(5, speed));
  };

  const getTargets = () => {
    // Prefer explicit data-hint targets
    let nodes = Array.from(document.querySelectorAll('[data-hint]'));
    if (nodes.length > 0) {
      debugLog('[SideHint] Found explicit hint targets:', nodes.length);
      return nodes;
    }
    // Fallback: try common sections/headings with ids
    const fallback = Array.from(document.querySelectorAll('section, main > div, [id]'))
      .filter(el => (el.clientHeight || 0) > 220);
    debugLog('[SideHint] No explicit targets. Using fallback pool:', fallback.length);
    return fallback;
  };

  // Resolve message for a given element (shared by IO and scroll paths)
  const resolveMessage = (el) => {
    const hintKey = el.getAttribute('data-hint-key');
    const elId = (el.getAttribute('id') || '').toLowerCase();
    if (hintKey === 'plans' || elId === 'pricing') {
      return {
        text: 'راحت باش! اگه خدای نکرده اکانتت به هر دلیلی مشکل پیدا کرد یا بن شد، همون لحظه بدون هیچ سوال اضافه‌ای برات یه اکانت تازه می‌فرستیم. ۲۴/۷ هم کنارتم!',
        source: 'plans-override',
      };
    }
    const attr = el.getAttribute('data-hint');
    if (attr) return { text: attr, source: 'attribute' };
    const h = el.querySelector('h1, h2, h3');
    if (h && h.textContent) return { text: `در حال مشاهده: ${h.textContent.trim()}`, source: 'heading' };
    const aria = el.getAttribute('aria-label');
    if (aria) return { text: `در حال مشاهده: ${aria}`, source: 'aria' };
    return { text: 'در حال مشاهده این بخش از صفحه هستید.', source: 'fallback' };
  };

  const hasShown = (hintId) => shownHintsRef.current.has(`${location.pathname}::${hintId}`);
  const markShown = (hintId) => shownHintsRef.current.add(`${location.pathname}::${hintId}`);

  const attachObserver = () => {
    // Detach previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const targets = getTargets();
    if (targets.length === 0) return;

    debugLog('[SideHint] Attaching observer. Targets:', targets.length);
    const options = { root: null, rootMargin: '0px 0px -20% 0px', threshold: [0.01, 0.25, 0.5] };
    const handle = (entries) => {
      // Pick the most visible intersecting entry
      const candidates = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => (b.intersectionRatio - a.intersectionRatio));
      if (candidates.length === 0) return;
      const top = candidates[0];
      const el = top.target;
      const hintId = el.getAttribute('data-hint-id') || el.getAttribute('id') || el.getAttribute('data-hint') || el.tagName + ':' + (el.className || '');
      debugLog('[SideHint][IO] Candidate:', { hintId, ratio: top.intersectionRatio.toFixed(2) });
      if (lastHintIdRef.current === hintId) return; // avoid repeats

      lastHintIdRef.current = hintId;
      const { text, source } = resolveMessage(el);
      if (source === 'plans-override') { debugLog('[SideHint] Using override message for plans/pricing'); }
      const s = 'right'; // Force right side per requirement
      const delay = Number(el.getAttribute('data-hint-delay') || 3000);
      const speed = Number(el.getAttribute('data-hint-speed') || 20);
      const duration = Number(el.getAttribute('data-hint-duration') || 6000);

      setSide(s === 'left' ? 'left' : 'right');
      setFullText(text);
      clearTimers();
      showTimeoutRef.current = setTimeout(() => {
        // Verify still visible
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const visibleY = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
        const ratio = Math.max(0, Math.min(1, visibleY / Math.max(1, rect.height)));
        if (ratio < 0.15 || hasShown(hintId)) return;
        markShown(hintId);
        debugLog('[SideHint] Showing popup for', hintId, 'with text:', text);
        setVisible(true);
        startTyping(text, speed, duration);
      }, Math.max(0, delay));
    };

    observerRef.current = new IntersectionObserver(handle, options);
    targets.forEach(t => observerRef.current.observe(t));

    // Fallback immediate trigger for most visible target on mount
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    let best = null;
    let bestRatio = 0;
    for (const el of targets) {
      const rect = el.getBoundingClientRect();
      const visibleY = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
      const ratio = Math.max(0, Math.min(1, visibleY / Math.max(1, rect.height)));
      if (ratio > bestRatio) { bestRatio = ratio; best = el; }
    }
    if (best && bestRatio > 0.01) {
      const { text, source } = resolveMessage(best);
      const delay = Number(best.getAttribute('data-hint-delay') || 2000);
      const speed = Number(best.getAttribute('data-hint-speed') || 20);
      const duration = Number(best.getAttribute('data-hint-duration') || 6000);
      lastHintIdRef.current = best.getAttribute('data-hint-id') || best.getAttribute('id') || text;
      setSide('right');
      setFullText(text);
      clearTimers();
      showTimeoutRef.current = setTimeout(() => {
        if (hasShown(lastHintIdRef.current)) return;
        markShown(lastHintIdRef.current);
        debugLog('[SideHint] Immediate fallback show for', lastHintIdRef.current, 'source:', source, 'text:', text);
        setVisible(true);
        startTyping(text, speed, duration);
      }, Math.max(0, delay));
    }
  };

  // Re-attach on route changes and on small DOM changes
  useEffect(() => {
    debugLog('[SideHint] Route changed to', location.pathname);
    // Try immediately and then once more after layout settles
    attachObserver();
    const t = setTimeout(() => attachObserver(), 500);
    return () => { clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // As an additional safety, compute active section on scroll/resize
  useEffect(() => {
    const computeActive = () => {
      const targets = getTargets();
      if (targets.length === 0) return;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      let best = null;
      let bestRatio = 0;
      for (const el of targets) {
        const rect = el.getBoundingClientRect();
        const visibleY = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
        const ratio = Math.max(0, Math.min(1, visibleY / Math.max(1, rect.height)));
        if (ratio > bestRatio) { bestRatio = ratio; best = el; }
      }
      if (!best) return;
      const hintId = best.getAttribute('data-hint-id') || best.getAttribute('id') || best.getAttribute('data-hint') || best.tagName + ':' + (best.className || '');
      if (lastHintIdRef.current === hintId) return;
      debugLog('[SideHint][Scroll] Active target', hintId, 'ratio', bestRatio.toFixed(2));
      lastHintIdRef.current = hintId;
      const { text, source } = resolveMessage(best);
      const delay = Number(best.getAttribute('data-hint-delay') || 2000);
      const speed = Number(best.getAttribute('data-hint-speed') || 20);
      const duration = Number(best.getAttribute('data-hint-duration') || 6000);
      setFullText(text);
      clearTimers();
      showTimeoutRef.current = setTimeout(() => {
        if (hasShown(hintId)) return;
        markShown(hintId);
        debugLog('[SideHint][Scroll] Showing from scroll compute. source:', source, 'text:', text);
        setVisible(true);
        startTyping(text, speed, duration);
      }, Math.max(0, delay));
    };

    const onScroll = () => {
      if (scrollTickingRef.current) return;
      scrollTickingRef.current = true;
      requestAnimationFrame(() => {
        scrollTickingRef.current = false;
        computeActive();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Initial compute after mount
    const t = setTimeout(() => computeActive(), 800);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      clearTimeout(t);
    };
  }, []);

  useEffect(() => () => clearTimers(), []);

  const positionClasses = 'right-3 md:right-6';

  // Mount flag for portals
  useEffect(() => { setMounted(true); }, []);

  useEffect(() => { debugLog('[SideHint][Render] visible:', visible, 'typed.len:', typedText.length); }, [visible, typedText]);

  // Developer helper to force a popup from console: window.showHint('text')
  useEffect(() => {
    window.showHint = (text = 'پیام آزمایشی سیستم راهنما') => {
      clearTimers();
      setVisible(true);
      startTyping(String(text), 18, 6000);
    };
    return () => { try { delete window.showHint; } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!mounted) return null;

  return createPortal(
    (
      <div
        className={`fixed top-1/2 ${positionClasses} transition-transform duration-300 pointer-events-auto`}
        style={{
          position: 'fixed',
          top: '50vh',
          right: '16px',
          transform: visible ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(140%)',
          opacity: visible ? 1 : 0,
          willChange: 'transform, opacity',
          zIndex: 2147483647,
        }}
        aria-live="polite"
      >
        <div
          className="backdrop-blur-md border border-white/20 rounded-2xl shadow-xl px-4 py-3 md:px-5 md:py-4"
          style={{ background: 'rgba(15,15,18,0.82)', minWidth: 280, maxWidth: 360, borderColor: 'rgba(255,255,255,0.3)' }}
          dir="rtl"
        >
          <div className="text-xs md:text-sm text-white/90 leading-relaxed">{typedText}</div>
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              onClick={() => setVisible(false)}
              className="text-white/70 hover:text-white text-xs md:text-sm"
              aria-label="بستن"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    ),
    document.body
  );
}

